import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Search, Filter, Eye, CheckCircle, Clock, XCircle, User, Mail, Phone, Calendar, DollarSign, FileText } from "lucide-react";
import { useRealtimeSubscription } from "@/hooks/use-realtime";
import { CompleteWithPaymentDialog } from "@/components/service-requests/CompleteWithPaymentDialog";

interface ServiceRequest {
  id: string;
  service_type: string;
  business_name: string;
  contact_name: string;
  email: string;
  phone: string | null;
  description: string;
  budget_range: string | null;
  preferred_start_date: string | null;
  status: string;
  assigned_to: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

const ServiceRequests = () => {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedRequest, setSelectedRequest] = useState<ServiceRequest | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);

  const { data: requests = [], isLoading: loading, refetch } = useQuery({
    queryKey: ["service-requests"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("service_requests")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as ServiceRequest[];
    },
  });

  // Enable realtime updates
  useRealtimeSubscription("service_requests", "service-requests");

  useEffect(() => {
    // Reset notification count when visiting this page
    const resetCount = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.rpc("reset_notification_count", {
          p_user_id: user.id,
          p_page_type: "service_requests",
        });
      }
    };
    resetCount();
  }, []);

  const updateRequestStatus = async (id: string, newStatus: string) => {
    // If completing, show payment dialog first
    if (newStatus === "completed") {
      const request = requests.find(r => r.id === id);
      setSelectedRequest(request || null);
      setPaymentDialogOpen(true);
      return;
    }

    setUpdatingStatus(true);
    try {
      const { error } = await supabase
        .from("service_requests")
        .update({ status: newStatus })
        .eq("id", id);

      if (error) throw error;

      // If accepting the request, create a project (only if doesn't exist)
      if (newStatus === "in_progress") {
        const request = requests.find(r => r.id === id);
        if (request) {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            // Check if project already exists for this service request
            const { data: existingProjects } = await supabase
              .from("projects")
              .select("id")
              .eq("service_request_id", id)
              .limit(1);

            if (!existingProjects || existingProjects.length === 0) {
              await supabase.from("projects").insert({
                name: `${request.service_type} - ${request.business_name}`,
                description: request.description,
                status: "active",
                service_request_id: id,
                user_id: user.id,
              });
            }
          }
        }
      }

      toast({
        title: "Status Updated",
        description: newStatus === "in_progress" ? "Request accepted and project created!" : "Request status has been updated successfully.",
      });

      refetch();
      if (selectedRequest?.id === id) {
        setSelectedRequest({ ...selectedRequest, status: newStatus });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const updateRequestNotes = async (id: string, notes: string) => {
    try {
      const { error } = await supabase
        .from("service_requests")
        .update({ notes })
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Notes Updated",
        description: "Notes have been saved successfully.",
      });

      refetch();
      if (selectedRequest?.id === id) {
        setSelectedRequest({ ...selectedRequest, notes });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { color: "bg-warning/10 text-warning border-warning/20", icon: Clock, label: "Pending" },
      in_progress: { color: "bg-primary/10 text-primary border-primary/20", icon: Loader2, label: "In Progress" },
      completed: { color: "bg-success/10 text-success border-success/20", icon: CheckCircle, label: "Completed" },
      rejected: { color: "bg-destructive/10 text-destructive border-destructive/20", icon: XCircle, label: "Rejected" }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge className={config.color}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const filteredRequests = requests.filter((request) => {
    const matchesSearch = 
      request.business_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.contact_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.service_type.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || request.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const statusCounts = {
    all: requests.length,
    pending: requests.filter(r => r.status === "pending").length,
    in_progress: requests.filter(r => r.status === "in_progress").length,
    completed: requests.filter(r => r.status === "completed").length,
    rejected: requests.filter(r => r.status === "rejected").length,
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="space-y-4 sm:space-y-6">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gradient">Service Requests</h1>
        <p className="text-sm md:text-base text-muted-foreground">Manage and track client service requests</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
        <Card className="glass-card p-3 md:p-4">
          <div className="text-xs md:text-sm text-muted-foreground mb-1">Total Requests</div>
          <div className="text-xl md:text-2xl font-bold">{statusCounts.all}</div>
        </Card>
        <Card className="glass-card p-3 md:p-4">
          <div className="text-xs md:text-sm text-warning mb-1">Pending</div>
          <div className="text-xl md:text-2xl font-bold text-warning">{statusCounts.pending}</div>
        </Card>
        <Card className="glass-card p-3 md:p-4">
          <div className="text-xs md:text-sm text-primary mb-1">In Progress</div>
          <div className="text-xl md:text-2xl font-bold text-primary">{statusCounts.in_progress}</div>
        </Card>
        <Card className="glass-card p-3 md:p-4">
          <div className="text-xs md:text-sm text-success mb-1">Completed</div>
          <div className="text-xl md:text-2xl font-bold text-success">{statusCounts.completed}</div>
        </Card>
        <Card className="glass-card p-3 md:p-4 col-span-2 md:col-span-1">
          <div className="text-xs md:text-sm text-destructive mb-1">Rejected</div>
          <div className="text-xl md:text-2xl font-bold text-destructive">{statusCounts.rejected}</div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="glass-card p-3 md:p-4">
        <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by business name, contact, email..."
              className="pl-10 input-modern text-sm md:text-base"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px] input-modern text-sm md:text-base">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Requests List */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground mt-4">Loading service requests...</p>
        </div>
      ) : filteredRequests.length === 0 ? (
        <Card className="glass-card p-12 text-center">
          <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No Requests Found</h3>
          <p className="text-muted-foreground">
            {searchQuery || statusFilter !== "all"
              ? "Try adjusting your filters"
              : "No service requests have been submitted yet"}
          </p>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredRequests.map((request) => (
            <Card key={request.id} className="glass-card-hover p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg sm:text-xl font-semibold">{request.business_name}</h3>
                    {getStatusBadge(request.status)}
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground mb-3">
                    {request.service_type} • Submitted {new Date(request.created_at).toLocaleDateString()}
                  </p>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{request.description}</p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 text-xs sm:text-sm">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span>{request.contact_name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="truncate">{request.email}</span>
                    </div>
                    {request.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span>{request.phone}</span>
                      </div>
                    )}
                    {request.budget_range && (
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        <span>{request.budget_range}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 w-full sm:w-auto sm:ml-4">
                  <Select
                    value={request.status}
                    onValueChange={(value) => updateRequestStatus(request.id, value)}
                    disabled={updatingStatus}
                  >
                    <SelectTrigger className="w-full sm:w-[140px] text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      setSelectedRequest(request);
                      setDetailsOpen(true);
                    }}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">Request Details</DialogTitle>
            <DialogDescription>
              Complete information about this service request
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-6">
              {/* Status Badge */}
              <div className="flex items-center justify-between">
                {getStatusBadge(selectedRequest.status)}
                <span className="text-sm text-muted-foreground">
                  ID: {selectedRequest.id.slice(0, 8)}...
                </span>
              </div>

              {/* Business Info */}
              <div className="space-y-4">
                <div>
                  <Label className="text-sm text-muted-foreground">Business Name</Label>
                  <p className="text-lg font-semibold">{selectedRequest.business_name}</p>
                </div>

                <div>
                  <Label className="text-sm text-muted-foreground">Service Type</Label>
                  <p className="text-lg">{selectedRequest.service_type}</p>
                </div>

                <div>
                  <Label className="text-sm text-muted-foreground">Project Description</Label>
                  <p className="text-base whitespace-pre-wrap">{selectedRequest.description}</p>
                </div>
              </div>

              {/* Contact Info */}
              <div className="border-t pt-4 space-y-3">
                <h4 className="font-semibold">Contact Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-muted-foreground">Contact Name</Label>
                    <p>{selectedRequest.contact_name}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Email</Label>
                    <p>{selectedRequest.email}</p>
                  </div>
                  {selectedRequest.phone && (
                    <div>
                      <Label className="text-sm text-muted-foreground">Phone</Label>
                      <p>{selectedRequest.phone}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Project Details */}
              <div className="border-t pt-4 space-y-3">
                <h4 className="font-semibold">Project Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedRequest.budget_range && (
                    <div>
                      <Label className="text-sm text-muted-foreground">Budget Range</Label>
                      <p>{selectedRequest.budget_range}</p>
                    </div>
                  )}
                  {selectedRequest.preferred_start_date && (
                    <div>
                      <Label className="text-sm text-muted-foreground">Preferred Start Date</Label>
                      <p>{new Date(selectedRequest.preferred_start_date).toLocaleDateString()}</p>
                    </div>
                  )}
                  <div>
                    <Label className="text-sm text-muted-foreground">Submitted On</Label>
                    <p>{new Date(selectedRequest.created_at).toLocaleString()}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Last Updated</Label>
                    <p>{new Date(selectedRequest.updated_at).toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* Internal Notes */}
              <div className="border-t pt-4 space-y-3">
                <h4 className="font-semibold">Internal Notes</h4>
                <Textarea
                  placeholder="Add internal notes about this request..."
                  value={selectedRequest.notes || ""}
                  onChange={(e) => setSelectedRequest({ ...selectedRequest, notes: e.target.value })}
                  rows={4}
                  className="input-modern"
                />
                <Button
                  onClick={() => updateRequestNotes(selectedRequest.id, selectedRequest.notes || "")}
                  className="btn-primary"
                >
                  Save Notes
                </Button>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setDetailsOpen(false)}
                  className="flex-1"
                >
                  Close
                </Button>
                <Button
                  className="btn-primary flex-1"
                  onClick={() => {
                    window.location.href = `mailto:${selectedRequest.email}?subject=Re: ${selectedRequest.service_type} Request&body=Hi ${selectedRequest.contact_name},%0D%0A%0D%0AThank you for your service request for ${selectedRequest.business_name}.`;
                  }}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Send Email
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <CompleteWithPaymentDialog
        open={paymentDialogOpen}
        onOpenChange={setPaymentDialogOpen}
        serviceRequest={selectedRequest}
        onSuccess={() => refetch()}
      />
    </div>
  );
};

export default ServiceRequests;
